import {Injectable} from '@angular/core';
import {BehaviorSubject,Observable} from "rxjs";
import {Balise} from "@domain/admin/bibliotheque/notification/balise";
import {NotificationService} from "@services/admin/bibliotheque/notification.service";
import {handleResult} from "@share/utils/handle-result";
import {filter,finalize,first} from "rxjs/operators";
import {Result} from "@domain/common/http/result";
import {ToastrService} from "ngx-toastr";
import {TranslateService} from "@ngx-translate/core";
import {ActivatedRoute,Router} from "@angular/router";
import {Location} from "@angular/common";
import {ConfirmService} from "@share/component/confirmation/confirm.service";

/**
 * Service de gestion du détail de la balise
 *
 * @author Lionel Gimenez
 * @date 16/03/2026
 */
@Injectable()
export class BaliseDetailService {
    /** Indique si le service est en train de sauvegarder */
    isSaving: boolean = false;

    /** Indicateur de création */
    isCreation: boolean = false;

    /** Modèle de la balise */
    private baliseSubject: BehaviorSubject<Balise> = new BehaviorSubject<Balise>(null);

    /** Getter balise */
    get balise$(): Observable<Balise> {
        return this.baliseSubject.asObservable();
    }

    /** Constructeur */
    constructor(
        private notificationService: NotificationService,
        private translateService: TranslateService,
        private toastrService: ToastrService,
        private confirmService: ConfirmService,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private location: Location
    ) {
    }

    /**
     * Charge la balise à partir de la route
     */
    loadBaliseByRoute(): void {
        //Chargement de l'id de la balise
        const idBalise : number = this.activatedRoute.snapshot.params.idBalise;

        //Si on est en création
        if (idBalise == 0) {
            this.loadNewBalise();
        } else {
            //Chargement de la balise
            this.loadBaliseById(idBalise);
        }
    }

    /**
     * Charge une nouvelle balise
     */
    loadNewBalise() {
        //On change l'indicateur
        this.isCreation = true;

        //Chargement de la nouvelle balise
        this.baliseSubject.next(new Balise());
    }

    /**
     * Charge la balise par l'id
     */
    loadBaliseById(idBalise: number) {
        this.notificationService.loadBalise(idBalise)
            .pipe(first())
            .subscribe((result: Result) => {
                this.baliseSubject.next(result.data.balise);
            });
    }

    /**
     * Sauvegarde la balise
     */
    save(): void {
        const balise = this.baliseSubject.getValue();

        //Enregistrer en cours
        this.isSaving = true;

        //Enregistrement de la balise
        this.notificationService
            .saveBalise(balise)
            .pipe(
                handleResult(this.translateService,this.toastrService),
                finalize(() => this.isSaving = false)
            ).subscribe((result: Result)=> {
                const balise = this.baliseSubject.getValue();

                //Si l'id est null, c'est une création
                if(balise.idBalise == null) {
                    //Mise à jour de l'id
                    const idBalise = result.data.idBalise;
                    balise.idBalise = idBalise;

                    //Passage en mode édition
                    this.isCreation = false;

                    //Mise à jour de l'url avec le nouvel id
                    this.location.replaceState(this.router.serializeUrl(
                        this.router.createUrlTree(['Admin/Bibliotheque/Notification/Balises', idBalise])
                    ));
                }
        });
    }

    /**
     * Suppression de la balise
     */
    deleteBalise(): void {
        const balise = this.baliseSubject.getValue();

        //Confirmation
        this.confirmService.showConfirm(this.translateService.instant('global.suppression.confirmation'))
            .pipe(filter(isConfirmed => isConfirmed))
            .subscribe(() => {
                //Suppression en cours
                this.isSaving = true;

                //Suppression de la balise
                this.notificationService.deleteBalise(balise.idBalise)
                    .pipe(
                        handleResult(this.translateService,this.toastrService,'global.success.suppression','global.errors.suppression'),
                        finalize(() => this.isSaving = false)
                    )
                    .subscribe(() => {
                        //Retour à la liste
                        this.router.navigate(['Admin/Bibliotheque/Notification/Balises'],{replaceUrl: true});
                    });
            });
    }
}